import { queryOptions } from '@tanstack/react-query'
import { supabase } from './client'
import type { ImageTransformOptions } from './image-transform'

export type { ImageTransformOptions } from './image-transform'

/**
 * Resolves the storage bucket for a given path.
 * Lumoo talent files live under `lumoo/` in the 'lumoo-talents' bucket.
 * Asset paths contain '/assets/' and belong to the 'assets' bucket.
 * All other paths belong to the 'generations' bucket.
 */
export function resolveBucket(
  path: string
): 'lumoo-talents' | 'assets' | 'generations' {
  if (path.startsWith('lumoo/')) {
    return 'lumoo-talents'
  }
  return path.includes('/assets/') ? 'assets' : 'generations'
}

export interface GetAssetUrlOptions {
  download?: boolean
  imageTransform?: ImageTransformOptions
}

/**
 * Generate a public or signed URL for a Supabase Storage asset
 *
 * @param bucket - The storage bucket name
 * @param path - The path to the asset within the bucket
 * @param isPublic - Whether the bucket is public or private
 * @param options - Optional URL generation options
 * @returns The asset URL
 * @throws Error if bucket/path is invalid or generation fails
 */
export async function getSupabaseAssetUrl(
  bucket: string,
  path: string,
  isPublic: boolean,
  options: GetAssetUrlOptions = {}
): Promise<string> {
  try {
    if (isPublic) {
      // For public buckets, get the public URL
      const { data } = supabase.storage.from(bucket).getPublicUrl(path, {
        transform: options.imageTransform,
        download: options.download,
      })

      return data.publicUrl
    } else {
      // For private buckets, create a signed URL (expires in 30 days)
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, 30 * 24 * 3600, {
          transform: options.imageTransform,
          download: options.download,
        })

      if (error) {
        throw new Error(
          `Failed to generate signed URL for ${bucket}/${path}: ${error.message}`
        )
      }

      if (!data?.signedUrl) {
        throw new Error(
          `Failed to generate signed URL for ${bucket}/${path}: No URL returned`
        )
      }

      return data.signedUrl
    }
  } catch (error) {
    throw new Error(`Failed to get asset URL for ${bucket}/${path}`, {
      cause: error,
    })
  }
}

/**
 * Batch generate URLs for multiple assets
 *
 * @param assets - Array of {bucket, path, isPublic} objects
 * @returns Array of URLs in the same order as input
 */
export async function getSupabaseAssetUrls(
  assets: Array<{
    bucket: string
    path: string
    isPublic: boolean
    options?: GetAssetUrlOptions
  }>
): Promise<string[]> {
  return Promise.all(
    assets.map((asset) =>
      getSupabaseAssetUrl(
        asset.bucket,
        asset.path,
        asset.isPublic,
        asset.options
      )
    )
  )
}

/**
 * Query options for checking if a bucket is public
 * Cached indefinitely since bucket configuration is static
 */
export const bucketPublicStatusQuery = (bucket: string) =>
  queryOptions({
    queryKey: ['storage', 'bucket', bucket, 'public-status'],
    queryFn: async (): Promise<boolean> => {
      const { data: bucketInfo, error } =
        await supabase.storage.getBucket(bucket)

      if (error) {
        throw new Error(`Failed to get bucket '${bucket}': ${error.message}`, {
          cause: error,
        })
      }

      if (!bucketInfo) {
        throw new Error(`Bucket '${bucket}' not found`)
      }

      return bucketInfo.public
    },
    staleTime: Infinity, // Bucket configuration never changes at runtime
  })
